import {
    CONSTANTS
} from '../Helpers/Configuration';
import get from 'lodash/get';
import {
    fetchEedmValues
} from '@nsr/react-query-builder-api';
import {
    getFilterSearchFieldsFormValues,
    navigateQuestions
} from './ApplicationHelpers';
import {
    buildModelAttributeObject,
    buildModelAttributeForFilter,
    mapAttributesToResults
} from './QueryHelpers';
import { getCodeFieldInfoForQuestion } from '@nsr/applicant-apply-shared/src/helpers/common';
import set from 'lodash/set';
const {
    controlTypes,
    questionPossibleValueTypes,
} = CONSTANTS;

// Fetch EEDM values for questions of type "checkbox" or "singlechoice" with dynamic values
export const fetchDynamicLists = async (api, template, supportedGraphqlModels) => {
    // No api, no template, no pages....
    if (!api || !template || !template?.pages?.length > 0) {
        return;
    }
    const promises = [];
    const context = { api, template, supportedGraphqlModels };
    const errors = [];

    await api.getRootTypes();
    navigateQuestions(context.template, (_p, _g, _s, question) => {
        if (isQuestionValidForQuery(question) && (
            question.type === controlTypes.checkbox ||
            question.type === controlTypes.singlechoice
        )) {
            promises.push(
                getPossibleValuesForQuestion(
                    question, context
                ).then(results => ({
                    question, results
                })).catch(error => ({
                    question, error
                }))
            );
        }
    });

    (await Promise.all(promises)).forEach(r => {
        const { question, results, error } = r;

        if (error) {
            console.error(`Failed to get dynamic list for question ${question.id}, model ${question?.possibleValues?.model}.`);
            console.error(error);
            errors.push(r);
        } else {
            question.possibleValues.values = results;
        }
    });

    return errors;
};

// Fetch EEDM values for a question of type "checkbox" or "singlechoice" with dynamic values
export const fetchDynamicListForQuestion = (api, question, supportedGraphqlModels, template) => {
    return getPossibleValuesForQuestion(
        question, { api, supportedGraphqlModels, template }
    );
};

// Fetch EEDM values for saved values of questions of type "searchandselect" with dynamic values
export const fetchSearchSelectSavedValues = async (api, template, application, supportedGraphqlModels) => {
    if (!api || !template || !application || !application.values) {
        return;
    }

    const promises = [];
    const errors = [];

    navigateQuestions(template, (p, s, _g, question) => {
        if (
            isQuestionValidForQuery(question) &&
            question.type === controlTypes.searchandselect
        ) {
            let idList;

            if (s.showRepetition) {
                idList = get(application.values, `${p.id}.${s.id}`, [])
                    .map(value => get(value, question.id, [])).flat();
            } else {
                idList = get(application.values, `${p.id}.${question.id}`, []);
            }

            idList = idList.filter((id) => !!id);

            if (idList && idList?.length > 0) {
                const config = getQueryObjectForQuestion(question);
                config.idList = idList;

                promises.push(
                    getDataAccessList(
                        api, config, supportedGraphqlModels
                    ).then(result => ({
                        question,
                        results: result?.list
                    })).catch(error => ({
                        question, error
                    }))
                );
            }
        }
    });

    (await Promise.all(promises)).forEach(r => {
        const { question, results, error } = r;

        if (error) {
            console.error(`Failed to get search and select saved value for question ${question.id}, model ${question?.possibleValues?.model}.`);
            console.error(error);
            errors.push(r);
        } else {
            const codeFieldInfo = getCodeFieldInfoForQuestion(question);
            const { possibleValues } = question;
            const resultValues = results.map(
                r => getPossibleValueFromRecord(
                    r,
                    possibleValues.modelAttribute,
                    codeFieldInfo?.codeFieldPath
                )
            );

            if (Array.isArray(possibleValues.values)) {
                possibleValues.values = possibleValues.values.concat(resultValues);
            } else {
                possibleValues.values = resultValues;
            }
        }
    });

    return errors;
};

// Fetch EEDM values when typing into "searchandselect" control
export const fetchValuesOnSearch = async (api, question, filterText, supportedGraphqlModels) => {
    const { possibleValues } = question || {};

    if (!api || !possibleValues || !possibleValues.type || possibleValues.type !== questionPossibleValueTypes.dynamic ||
        !possibleValues.model || !possibleValues.modelAttribute || !supportedGraphqlModels[possibleValues.model]) {
        return [];
    }

    try {
        return await getPossibleValuesForQuestion(
            question, { supportedGraphqlModels, api, filterText }
        );
    } catch (err) {
        console.error(`Error fetching search values for question ${question.id}, model ${question.possibleValues.model}`);
        console.error(err);
        throw err;
    }
};

export const fetchFilterSearchRecord = async (api, template, applicationValues = {}, supportedGraphqlModels) => {
    if (!api || !template || !supportedGraphqlModels) {
        return;
    }

    const filterSearchFields = getFilterSearchFieldsFormValues(template, applicationValues);
    const promises = [];
    const errors = [];

    if (filterSearchFields.length) {
        await api.getRootTypes();

        for (const field of filterSearchFields) {
            const { question } = field;

            promises.push(
                fetchFilterSearchRecordsForQuestion(
                    api, field, supportedGraphqlModels
                ).then(results => ({
                    question, results
                })).catch(error => ({
                    question, error
                }))
            );
        }
    
        (await Promise.all(promises)).forEach(r => {
            const { question, results, error } = r;

            if (error) {
                console.error(`Unable to fetch filtered search record for question ${question?.id}, model ${question?.filteredSearch?.model}`);
                console.error(error);
                errors.push(error);
            } else {
                const { possibleValues } = question;

                if (possibleValues.values) {
                    possibleValues.values = possibleValues.values.concat(results);
                } else {
                    possibleValues.values = results;
                }
            }
        });
    }

    return errors;
};

const fetchFilterSearchRecordsForQuestion = async (api, field, supportedGraphqlModels) => {
    const model = field.question.filteredSearch.model;
    const displayAttributes = field.question.filteredSearch?.displayAttributes ?? [];
    const attributeNames = buildModelAttributeObject(displayAttributes);
    const { codeFieldPath } = getCodeFieldInfoForQuestion(field.question) ?? {};
    const { graphType } = supportedGraphqlModels[model] ?? {};

    if (!graphType) {
        throw new Error(`Model ${model} is not supported by the Ethos publisher application`);
    }

    if (codeFieldPath) {
        set(attributeNames, codeFieldPath, true);
    }

    if (field?.value?.length) {
        field.value = field.value.map(v => {
            if (v.includes('_noRecordFound')) {
                return v.replace('_noRecordFound', '');
            } else {
                return v;
            }
        });
    }

    const result = await api?.getValuesByIds(
        graphType, field.value, attributeNames
    );

    if (Array.isArray(result?.list)) {
        return mapAttributesToResults(
            displayAttributes, result.list, codeFieldPath
        );
    } else {
        return [];
    }
};

export const isQuestionValidForQuery = q => (
    q.possibleValues?.type === questionPossibleValueTypes.dynamic &&
    q.possibleValues?.model && q.possibleValues?.modelAttribute
);

export const getPossibleValueFromRecord = (record, labelAttr, codePath) => ({
    value: record.id,
    label: record[labelAttr],
    code: get(record, codePath)
});

export const getQueryObjectForQuestion = (q, filterText) => {
    const codeFieldInfo = getCodeFieldInfoForQuestion(q);
    const queryObj = {
        model: q.possibleValues.model,
        sort: q.possibleValues.modelAttribute,
        filterQuery: q.possibleValues.filterQuery,
        attributes: [q.possibleValues.modelAttribute],
        filterText,
        filterAttribute: filterText && q.possibleValues.modelAttribute
    };

    if (codeFieldInfo) {
        queryObj.attributes.push(codeFieldInfo.codeField);
    }

    if (q.attributes) {
        queryObj.attributes = q.attributes;
    }

    return queryObj;
};

export const getDataAccessList = async (api, config, supportedGraphqlModels) => {
    const graphType = supportedGraphqlModels?.[config.model]?.graphType;

    if (graphType) {
        const graphApi = (
            api.getGraphApi && await api.getGraphApi()
        ) || api.graphApi;
        let results;

        if (config.idList) {
            results = await api.getValuesByIds(
                graphType,
                config.idList,
                config.attributes
            );
        } else if (config.filterQuery) {
            results = await fetchEedmValues(
                graphApi,
                graphType,
                config.filterQuery,
                config.attributes,
                {
                    order: 'asc',
                    orderBy: config.sort,
                    page: 0,
                    rowsPerPage: 10000
                },
                config.filterText || '',
                config.filterAttribute || '',
                config.includeTotalCount || false
            );
        } else {
            const filter = config.simpleFilter || (
                config.filterText && config.filterAttribute &&
                buildModelAttributeForFilter(config.filterAttribute, config.filterText)
            ) || {};

            results = await api.getAttributeValues(
                graphType,
                config.attributes,
                filter,
                [config.sort],
                {},
                config.includeTotalCount || false
            );
        }

        return results || { list: [], totalCount: 0 };
    } else {
        return { list: [], totalCount: 0 };
    }
};

export const getResultsForQuestion = async (question, context) => {
    let rawList = await getDataAccessList(
        context.api,
        getQueryObjectForQuestion(question, context.filterText),
        context.supportedGraphqlModels
    );

    // Post Fetch Processing
    if (rawList?.list?.length) {
        // Academic-Catalog / Academic-Programs Filtering
        if (question.possibleValues.model === CONSTANTS.academicCatalogs.programModel && 
            !!context.template?.settings?.academicCatalogFilter?.id && 
            context.supportedGraphqlModels[CONSTANTS.academicCatalogs.model]) {
            let programVersion = await context.api.getMaxVersionByGraphType(context.supportedGraphqlModels[CONSTANTS.academicCatalogs.programModel].graphType);
            const catalogQuestion = {            
                possibleValues: {
                    model: CONSTANTS.academicCatalogs.model,
                    modelAttribute: CONSTANTS.academicCatalogs.attributes[0]
                }
            };

            let catalogQueryObj = getQueryObjectForQuestion(catalogQuestion);
            catalogQueryObj.simpleFilter = JSON.parse(JSON.stringify(CONSTANTS.academicCatalogs.filter).replace('xx__filterid__xx', context.template.settings.academicCatalogFilter?.id));
            catalogQueryObj.attributes = [CONSTANTS.academicCatalogs.attributes[0], CONSTANTS.academicCatalogs.attributes[1].replace('__ver__', programVersion)];
        
            const rawCatalogList = await getDataAccessList(
                context.api,
                catalogQueryObj,
                context.supportedGraphqlModels
            );

            if (rawCatalogList?.list?.length) {
                const programPropertyName = Object.keys(rawCatalogList.list[0]).find(p => p.includes('academicPrograms'));
                if (programPropertyName) {
                    const progIds = rawCatalogList.list[0]?.[programPropertyName]?.map(p => p.id) || [];
                    rawList.list = rawList.list.filter(res => progIds.includes(res.id));
                }
                else {
                    //Return nothing since the academic catalog came back without an academicPrograms property
                    rawList.list = [];
                }
            } 
        }
    }
    return rawList;
};

export const getPossibleValuesForQuestion = async (question, context) => {
    const codeFieldInfo = getCodeFieldInfoForQuestion(question);
    const results = await getResultsForQuestion(question, context) || [];
    return results.list?.map(it => getPossibleValueFromRecord(
        it,
        question.possibleValues.modelAttribute,
        codeFieldInfo?.codeFieldPath
    ));
};
